// ============================================
// useUsers Hook - User Management Data Fetching
// ============================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, type UserListItem, type CreateUserInput, type UpdateUserInput } from '@/lib/api';

export type { UserListItem, CreateUserInput, UpdateUserInput };

interface UseUsersParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: 'name' | 'email' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  enabled?: boolean;
}

/**
 * Hook for fetching the user list (admin only)
 *
 * @param params - Query parameters for filtering/sorting
 * @returns Query result with users data and pagination
 */
export function useUsers(params: UseUsersParams = {}) {
  const { enabled = true, ...queryParams } = params;

  return useQuery({
    queryKey: ['users', queryParams],
    queryFn: async () => {
      const response = await api.getUsers(queryParams);
      return response;
    },
    enabled,
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
  });
}

/**
 * Hook for fetching a single user by ID (admin only)
 *
 * @param id - User ID to fetch
 * @param enabled - Whether the query should run
 * @returns Query result with user data
 */
export function useUser(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['user', id],
    queryFn: async () => {
      if (!id) throw new Error('User ID is required');
      return api.getUser(id);
    },
    enabled: enabled && !!id,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook for creating a new user (admin only)
 *
 * @returns Mutation result with create function
 */
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateUserInput) => {
      return api.createUser(data);
    },
    onSuccess: () => {
      toast.success('User created successfully');
      // Invalidate users list to refresh data
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : 'Failed to create user');
    },
  });
}

/**
 * Hook for updating an existing user (admin only)
 *
 * @returns Mutation result with update function
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateUserInput }) => {
      return api.updateUser(id, data);
    },
    onSuccess: () => {
      toast.success('User updated successfully');
      // Invalidate users list to refresh data
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : 'Failed to update user');
    },
  });
}

/**
 * Hook for deleting (deactivating) a user (admin only)
 * Performs a soft delete - the user is deactivated, not permanently removed.
 *
 * @returns Mutation result with delete function
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return api.deleteUser(id);
    },
    onSuccess: () => {
      toast.success('User deleted successfully');
      // Invalidate users list to refresh data
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : 'Failed to delete user');
    },
  });
}
